import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, Database, Cloud, Cpu, Shield, CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_ICON = {
  healthy: <CheckCircle size={16} className="text-emerald-400" />,
  configured: <CheckCircle size={16} className="text-emerald-400" />,
  connected: <CheckCircle size={16} className="text-emerald-400" />,
  set: <CheckCircle size={16} className="text-emerald-400" />,
  degraded: <AlertCircle size={16} className="text-amber-400" />,
  unavailable: <XCircle size={16} className="text-red-400" />,
  unhealthy: <XCircle size={16} className="text-red-400" />,
  not_configured: <XCircle size={16} className="text-slate-500" />,
  missing: <XCircle size={16} className="text-red-400" />,
};

export default function SuperAdminHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const token = localStorage.getItem('superAdminToken');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  const fetchHealth = async (showToast) => {
    if (showToast) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/platform/system/health`, { headers: getHeaders() });
      setHealth(res.data);
      if (showToast) toast.success('Health check complete');
    } catch { toast.error('Failed to check health'); }
    setLoading(false);
    setRefreshing(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchHealth(false); }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" /></div>;

  const checks = health?.checks || {};
  const overall = health?.status || 'unknown';

  return (
    <div className="space-y-6 max-w-5xl" data-testid="health-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>System Health</h1>
          <p className="text-slate-400 mt-1">Monitor the health of all platform services</p>
        </div>
        <Button onClick={() => fetchHealth(true)} disabled={refreshing} className="bg-slate-700 hover:bg-slate-600 text-white" data-testid="refresh-health-btn">
          <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      {/* Overall Status */}
      <Card className={`border ${overall === 'healthy' ? 'bg-emerald-900/20 border-emerald-700/50' : 'bg-amber-900/20 border-amber-700/50'}`}>
        <CardContent className="p-6 flex items-center gap-4">
          {overall === 'healthy' ? <CheckCircle size={32} className="text-emerald-400" /> : <AlertCircle size={32} className="text-amber-400" />}
          <div>
            <p className="text-lg font-bold text-white" data-testid="overall-status">System Status: {overall === 'healthy' ? 'All Systems Operational' : 'Some Services Degraded'}</p>
            <p className="text-xs text-slate-400">Last checked: {health?.timestamp?.slice(0, 19).replace('T', ' ')} UTC</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* MongoDB */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-base"><Database size={18} />MongoDB</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300">Connection</span>
              <div className="flex items-center gap-1">{STATUS_ICON[checks.mongodb?.status]}<span className="text-xs text-slate-400">{checks.mongodb?.status}</span></div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300">Platform DB</span>
              <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">{checks.mongodb?.platform_db || 'unknown'}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300">Tenant Databases</span>
              <span className="text-sm font-bold text-white">{checks.mongodb?.tenant_databases || 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* Object Storage */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-base"><Cloud size={18} />Object Storage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300">Status</span>
              <div className="flex items-center gap-1">{STATUS_ICON[checks.object_storage?.status]}<span className="text-xs text-slate-400">{checks.object_storage?.status}</span></div>
            </div>
            {checks.object_storage?.error && <p className="text-xs text-red-400">{checks.object_storage.error}</p>}
          </CardContent>
        </Card>

        {/* AI Service */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-base"><Cpu size={18} />AI Service</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300">Status</span>
              <div className="flex items-center gap-1">{STATUS_ICON[checks.ai_service?.status]}<span className="text-xs text-slate-400">{checks.ai_service?.status}</span></div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300">Provider</span>
              <span className="text-xs text-slate-400">{checks.ai_service?.provider}</span>
            </div>
          </CardContent>
        </Card>

        {/* Payment Gateways */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-base"><Shield size={18} />Payment Gateways</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300">Stripe</span>
              <div className="flex items-center gap-1">{STATUS_ICON[checks.payment_gateways?.stripe]}<span className="text-xs text-slate-400">{checks.payment_gateways?.stripe}</span></div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300">Razorpay</span>
              <div className="flex items-center gap-1">{STATUS_ICON[checks.payment_gateways?.razorpay]}<span className="text-xs text-slate-400">{checks.payment_gateways?.razorpay}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Environment Variables */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base">Environment Configuration</CardTitle>
          <CardDescription className="text-slate-400">Status of critical environment variables</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {checks.environment && Object.entries(checks.environment).map(([key, status]) => (
              <div key={key} className="flex items-center gap-2 p-2 bg-slate-900 rounded border border-slate-700">
                {STATUS_ICON[status]}
                <div>
                  <p className="text-xs font-mono text-white">{key}</p>
                  <p className="text-[10px] text-slate-500">{status}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Data Stats */}
      {checks.data && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base">Platform Data</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(checks.data).map(([key, value]) => (
                <div key={key} className="text-center p-3 bg-slate-900 rounded border border-slate-700">
                  <p className="text-2xl font-bold text-white">{value}</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">{key}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
